import React from "react";
import type { ChartComponentProps } from "./types";
import { ChartContainer } from "./ChartContainer";
import { useChartAnimation } from "./useChartAnimation";
import {
  px,
  FONT,
  formatChartValue,
  estimateTextWidth,
  useAvailableWidth,
  computeYAxis,
} from "./chartUtils";
import { E } from "./chartColors";
import { ChartTitle } from "./ChartTitle";

const BAR_OPACITY = 0.7;
const BAR_RADIUS = 8;
const BAR_WIDTH = 49;
const BAR_GAP = 8;
const GROUP_WIDTH = BAR_WIDTH * 2 + BAR_GAP;
const MIN_GROUP_GAP = 12;
const FIGMA_W = 786;
const FIGMA_PAD_LEFT = 80;

export const GroupedComparisonBarChart: React.FC<ChartComponentProps> = ({
  chartData,
  ctx,
}) => {
  const { S } = ctx;
  const anim = useChartAnimation(ctx, chartData);
  const groups = chartData.groups || [];
  const seriesNames = chartData.series || ["Series A", "Series B"];
  const seriesAName = seriesNames[0] || "Series A";
  const seriesBName = seriesNames[1] || "Series B";
  const [measureRef, availPx] = useAvailableWidth();

  if (groups.length === 0) {
    return null;
  }

  const allValues = groups.flatMap((g) => g.values || []);
  const {
    yMin,
    yMax,
    ticks: yTickValues,
    hasNeg,
  } = computeYAxis(allValues, { includeZero: true });

  const padTop = 12;
  const plotH = 340;
  const getY = (v: number) => padTop + (1 - (v - yMin) / (yMax - yMin)) * plotH;
  const zeroY = getY(0);

  const formatVal = (v: number) => formatChartValue(v);

  const maxLabelWidth = Math.max(
    ...yTickValues.map((v) => estimateTextWidth(formatVal(v), 20)),
  );
  const padLeft = Math.max(FIGMA_PAD_LEFT, Math.ceil(maxLabelWidth) + 24);

  const W_from_container = availPx > 0 ? Math.round(availPx / S) : FIGMA_W;
  const contentMinW = padLeft + groups.length * (GROUP_WIDTH + MIN_GROUP_GAP);
  const W = Math.max(FIGMA_W, contentMinW, W_from_container);
  const plotW = W - padLeft;
  const H = padTop + plotH + 72;

  const cellW = plotW / groups.length;

  return (
    <ChartContainer ctx={ctx} chartData={chartData} width={W}>
      <div ref={measureRef} style={{ width: "100%" }}>
        {chartData.label && (
          <ChartTitle label={chartData.label} unit={chartData.unit} S={S} />
        )}

        {/* Legend */}
        <div
          style={{
            display: "flex",
            gap: px(44, S),
            alignItems: "center",
            marginBottom: px(16, S),
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: px(16, S),
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: px(16, S),
                height: px(16, S),
                borderRadius: "50%",
                backgroundColor: E.cyan,
                opacity: BAR_OPACITY,
              }}
            />
            <span
              style={{
                fontFamily: FONT,
                fontSize: px(24, S),
                fontWeight: 400,
                lineHeight: `${px(32, S)}px`,
                color: E.t1,
              }}
            >
              {seriesAName}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              gap: px(16, S),
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: px(16, S),
                height: px(16, S),
                borderRadius: "50%",
                backgroundColor: E.orange,
                opacity: BAR_OPACITY,
              }}
            />
            <span
              style={{
                fontFamily: FONT,
                fontSize: px(24, S),
                fontWeight: 400,
                lineHeight: `${px(32, S)}px`,
                color: E.t1,
              }}
            >
              {seriesBName}
            </span>
          </div>
        </div>

        <svg viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
          {yTickValues.map((val, i) => {
            const y = getY(val);
            return (
              <g key={i}>
                <line
                  x1={padLeft}
                  y1={y}
                  x2={W}
                  y2={y}
                  stroke={E.chartGrid}
                  strokeWidth={1}
                />
                <text
                  x={padLeft - 8}
                  y={y + 7}
                  textAnchor="end"
                  fill={E.muted}
                  fontSize={20}
                  fontFamily={FONT}
                  fontWeight={400}
                >
                  {formatVal(val)}
                </text>
              </g>
            );
          })}

          {hasNeg && (
            <line
              x1={padLeft}
              y1={zeroY}
              x2={W}
              y2={zeroY}
              stroke={E.muted}
              strokeWidth={1.5}
              strokeOpacity={0.6}
            />
          )}

          {groups.map((group, i) => {
            const stagger = anim.stagger(i, 0.12);
            const groupX = padLeft + cellW * i + (cellW - GROUP_WIDTH) / 2;

            const valA = group.values?.[0] ?? 0;
            const valB = group.values?.[1] ?? 0;
            const rawHA = Math.abs(getY(valA) - zeroY);
            const rawHB = Math.abs(getY(valB) - zeroY);
            const hA = rawHA * stagger.progress;
            const hB = rawHB * stagger.progress;
            const isNegA = valA < 0;
            const isNegB = valB < 0;
            const yA = isNegA ? zeroY : zeroY - hA;
            const yB = isNegB ? zeroY : zeroY - hB;
            const labelYA = isNegA ? yA + hA + 24 : yA - 8;
            const labelYB = isNegB ? yB + hB + 24 : yB - 8;

            const xB = groupX + BAR_WIDTH + BAR_GAP;

            return (
              <g key={i} opacity={stagger.opacity}>
                <text
                  x={groupX + BAR_WIDTH / 2}
                  y={labelYA}
                  textAnchor="middle"
                  fill={E.t3}
                  fontSize={20}
                  fontFamily={FONT}
                  fontWeight={500}
                >
                  {formatVal(valA)}
                </text>
                <rect
                  x={groupX}
                  y={yA}
                  width={BAR_WIDTH}
                  height={Math.max(hA, 1)}
                  rx={BAR_RADIUS}
                  ry={BAR_RADIUS}
                  fill={E.bgPage}
                />
                <rect
                  x={groupX}
                  y={yA}
                  width={BAR_WIDTH}
                  height={Math.max(hA, 1)}
                  rx={BAR_RADIUS}
                  ry={BAR_RADIUS}
                  fill={E.cyan}
                  fillOpacity={BAR_OPACITY}
                />

                <text
                  x={xB + BAR_WIDTH / 2}
                  y={labelYB}
                  textAnchor="middle"
                  fill={E.t3}
                  fontSize={20}
                  fontFamily={FONT}
                  fontWeight={500}
                >
                  {formatVal(valB)}
                </text>
                <rect
                  x={xB}
                  y={yB}
                  width={BAR_WIDTH}
                  height={Math.max(hB, 1)}
                  rx={BAR_RADIUS}
                  ry={BAR_RADIUS}
                  fill={E.bgPage}
                />
                <rect
                  x={xB}
                  y={yB}
                  width={BAR_WIDTH}
                  height={Math.max(hB, 1)}
                  rx={BAR_RADIUS}
                  ry={BAR_RADIUS}
                  fill={E.orange}
                  fillOpacity={BAR_OPACITY}
                />
              </g>
            );
          })}

          {groups.map((group, i) => {
            const cx = padLeft + cellW * i + cellW / 2;
            const labelW = Math.min(cellW - 4, 180);
            return (
              <foreignObject
                key={i}
                x={cx - labelW / 2}
                y={padTop + plotH + 8}
                width={labelW}
                height={56}
              >
                <div
                  style={{
                    width: "100%",
                    textAlign: "center",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                    fontFamily: FONT,
                    fontSize: 20,
                    fontWeight: 400,
                    color: E.muted,
                    lineHeight: "24px",
                  }}
                >
                  {group.label}
                </div>
              </foreignObject>
            );
          })}
        </svg>
      </div>
    </ChartContainer>
  );
};
